import axios from 'axios'
import api from '..'
import { AUTH_ACCESS_TOKEN_KEY } from '@/constants'

export type ExplorerSource = 'v1' | 'v2'

export interface CollectionInfo {
  name: string
  type?: string
}

export interface DocumentPage {
  count: number
  page: number
  perPage: number
  result: any[]
}

export type AggregationStatus = 'waiting' | 'active' | 'completed' | 'failed'

export interface AggregationJob {
  id: string
  collection: string
  pipeline: any[]
  status: AggregationStatus
  progress: number
  result?: any
  resultCount?: number
  truncated?: boolean
  error?: string
  createdAt?: string
  finishedAt?: string
}

export interface ListDocumentsParams {
  page?: number
  perPage?: number
  filter?: string
  sort?: string
  projection?: string
}

const V1_BASE_URL = (process.env.VUE_APP_V1_API_BASE_URL || '/api').replace(
  /\/$/,
  ''
)

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  url: string
  params?: Record<string, any>
  data?: any
}

/**
 * Contrat front <-> back de l'explorateur de données.
 * Tous les endpoints vivent sous `<base>/data-explorer`.
 */
export class DataExplorerService {
  private static async request<T = any>(
    source: ExplorerSource,
    options: RequestOptions
  ): Promise<T> {
    if (source === 'v1') {
      const token = sessionStorage.getItem(AUTH_ACCESS_TOKEN_KEY)
      const response = await axios.request({
        baseURL: `${V1_BASE_URL}/data-explorer`,
        url: options.url,
        method: options.method,
        // `referer=v2` indique à l'API v1 que l'utilisateur a déjà été
        // authentifié côté v2 et évite une seconde vérification 2FA.
        params: { ...(options.params || {}), referer: 'v2' },
        data: options.data,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      })
      return response.data
    }

    const response = await api.request({
      url: `/data-explorer${options.url}`,
      method: options.method,
      params: options.params,
      data: options.data
    })
    return response.data
  }

  static listCollections(source: ExplorerSource): Promise<CollectionInfo[]> {
    return this.request(source, { method: 'GET', url: '/collections' })
  }

  static listDocuments(
    source: ExplorerSource,
    collection: string,
    params: ListDocumentsParams
  ): Promise<DocumentPage> {
    return this.request(source, {
      method: 'GET',
      url: `/collections/${encodeURIComponent(collection)}/documents`,
      params
    })
  }

  static getDocument(
    source: ExplorerSource,
    collection: string,
    id: string
  ): Promise<any> {
    return this.request(source, {
      method: 'GET',
      url: `/collections/${encodeURIComponent(
        collection
      )}/documents/${encodeURIComponent(id)}`
    })
  }

  static createDocument(
    source: ExplorerSource,
    collection: string,
    document: any
  ): Promise<{ insertedId: string; id: string }> {
    return this.request(source, {
      method: 'POST',
      url: `/collections/${encodeURIComponent(collection)}/documents`,
      data: { document }
    })
  }

  static updateDocument(
    source: ExplorerSource,
    collection: string,
    id: string,
    document: any
  ): Promise<any> {
    return this.request(source, {
      method: 'PUT',
      url: `/collections/${encodeURIComponent(
        collection
      )}/documents/${encodeURIComponent(id)}`,
      data: { document }
    })
  }

  static deleteDocument(
    source: ExplorerSource,
    collection: string,
    id: string
  ): Promise<{ deleted: boolean; id: string }> {
    return this.request(source, {
      method: 'DELETE',
      url: `/collections/${encodeURIComponent(
        collection
      )}/documents/${encodeURIComponent(id)}`
    })
  }

  static runAggregation(
    source: ExplorerSource,
    collection: string,
    pipeline: any[],
    options: { allowDiskUse?: boolean; maxTimeMS?: number } = {}
  ): Promise<{ jobId: string }> {
    return this.request(source, {
      method: 'POST',
      url: `/collections/${encodeURIComponent(collection)}/aggregations`,
      data: { pipeline, ...options }
    })
  }

  static getAggregation(
    source: ExplorerSource,
    jobId: string
  ): Promise<AggregationJob> {
    return this.request(source, {
      method: 'GET',
      url: `/aggregations/${encodeURIComponent(jobId)}`
    })
  }

  static listAggregations(
    source: ExplorerSource,
    collection: string
  ): Promise<AggregationJob[]> {
    return this.request(source, {
      method: 'GET',
      url: `/collections/${encodeURIComponent(collection)}/aggregations`
    })
  }
}
